import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  Users as UsersIcon,
  RefreshCw,
  Download,
  Search,
  X,
  BadgeCheck,
  Trash2,
  Mail,
  Phone,
  Inbox,
  TrendingUp,
  ArrowRightLeft,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-coral/15 text-coral border-coral/30' },
  { value: 'contacted', label: 'Contacted', color: 'bg-ocean/15 text-ocean border-ocean/30' },
  { value: 'quoted', label: 'Quoted', color: 'bg-terracotta/15 text-terracotta border-terracotta/30' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  { value: 'declined', label: 'Declined', color: 'bg-maroon/10 text-maroon/60 border-maroon/20' },
];
const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

// CSV helpers
const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/\r\n|\r|\n/g, ' ').trim();
  return /[",;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (rows, filename) => {
  const headers = ['Created', 'Name', 'Email', 'Phone', 'Destination Interest', 'Group Size', 'Preferred Dates', 'Status', 'Lead ID', 'Admin Notes', 'Original Message'];
  const body = rows.map((c) => [
    new Date(c.created_at).toISOString(),
    c.name,
    c.email,
    c.phone || '',
    c.destination_interest || '',
    c.group_size || '',
    c.preferred_dates || '',
    c.status || 'pending',
    c.lead_id || '',
    c.admin_notes || '',
    c.message || '',
  ].map(csvEscape).join(','));
  const csv = [headers.join(','), ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const StatTile = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-white border border-maroon/10 rounded-sm p-4 flex items-start gap-3" data-testid={testId}>
    <div className="rounded-sm bg-terracotta/10 text-terracotta p-2 shrink-0">
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-maroon/50 font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold text-maroon leading-tight mt-0.5">{value}</p>
      {sub ? <p className="text-xs text-maroon/50 mt-0.5">{sub}</p> : null}
    </div>
  </div>
);

const ConsultationsManager = () => {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertPackageId, setConvertPackageId] = useState('');
  const [convertNotes, setConvertNotes] = useState('');
  const [converting, setConverting] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/consultations/stats`, auth());
      setStats(res.data);
    } catch {
      // non-essential
    }
  };

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/partner-packages`);
      setPackages(res.data || []);
    } catch {
      setPackages([]);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const res = await axios.get(`${API}/consultations?${params.toString()}`, auth());
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); fetchStats(); }, []);
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [filter, startDate, endDate]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((c) =>
      [c.name, c.email, c.phone, c.destination_interest, c.message, c.admin_notes]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [items, search]);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.put(`${API}/consultations/${id}`, { status }, auth());
      setItems((xs) => xs.map((c) => (c.id === id ? res.data : c)));
      toast.success(`Marked as ${status}`);
      fetchStats();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const updateNotes = async (id, admin_notes) => {
    try {
      const res = await axios.put(`${API}/consultations/${id}`, { admin_notes }, auth());
      setItems((xs) => xs.map((c) => (c.id === id ? res.data : c)));
    } catch {
      toast.error('Failed to save note');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this consultation permanently?')) return;
    try {
      await axios.delete(`${API}/consultations/${id}`, auth());
      setItems((xs) => xs.filter((c) => c.id !== id));
      toast.success('Deleted');
      fetchStats();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openConvert = (consult) => {
    setConvertTarget(consult);
    setConvertPackageId('');
    setConvertNotes('');
    setConvertOpen(true);
  };

  const submitConvert = async () => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      const body = {};
      if (convertPackageId) body.package_id = convertPackageId;
      if (convertNotes.trim()) body.notes = convertNotes.trim();
      const res = await axios.post(`${API}/consultations/${convertTarget.id}/convert-to-lead`, body, auth());
      toast.success(`Converted to lead${res.data?.package_title ? ` — ${res.data.package_title}` : ''}`);
      setConvertOpen(false);
      fetchItems();
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error('Nothing to export');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCSV(filtered, `chimba-consultations-${stamp}.csv`);
    toast.success(`Exported ${filtered.length} consultations`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon">Consultations</h2>
          <p className="text-maroon/60 text-sm mt-1">
            Top-of-funnel inquiries. Convert qualified ones into Leads and track them through the booking funnel.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => { fetchItems(); fetchStats(); }} variant="outline" className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-1" data-testid="refresh-consultations-btn">
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button onClick={exportCsv} className="btn-primary inline-flex items-center gap-2" data-testid="export-consultations-csv-btn">
            <Download size={16} /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="consultations-stats-panel">
          <StatTile icon={Inbox} label="Total inquiries" value={stats.total} testId="consult-stat-total" />
          <StatTile icon={CheckCircle2} label="Pending review" value={stats.by_status?.pending || 0} testId="consult-stat-pending" />
          <StatTile icon={TrendingUp} label="Conversion rate" value={`${((stats.conversion_rate || 0) * 100).toFixed(1)}%`} sub={`${stats.converted_to_lead || 0} converted to lead`} testId="consult-stat-conversion" />
          <StatTile icon={BadgeCheck} label="Member-linked" value={stats.members_linked || 0} sub="Tied to a customer account" testId="consult-stat-members" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-sand/30 border border-maroon/10 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 flex-wrap">
          {[{ value: 'all', label: 'All' }, ...STATUS_OPTIONS].map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setFilter(o.value)}
              className={`text-xs uppercase tracking-wider font-semibold px-3 py-1.5 rounded-sm border transition-colors ${
                filter === o.value
                  ? 'bg-maroon text-white border-maroon'
                  : 'bg-white text-maroon border-maroon/20 hover:bg-maroon/5'
              }`}
              data-testid={`filter-status-${o.value}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-2">
          <CalendarIcon size={14} className="text-maroon/60" />
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-sm border border-maroon/20 rounded-sm px-2 py-1.5" data-testid="filter-start-date" />
          <span className="text-xs text-maroon/50">→</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-sm border border-maroon/20 rounded-sm px-2 py-1.5" data-testid="filter-end-date" />
          {(startDate || endDate) && (
            <button type="button" onClick={() => { setStartDate(''); setEndDate(''); }} className="text-maroon/60 hover:text-maroon" aria-label="Clear dates"><X size={14} /></button>
          )}
        </div>
        <div className="inline-flex items-center gap-2 grow min-w-[200px]">
          <Search size={14} className="text-maroon/60" />
          <input
            type="text"
            placeholder="Search name, email, destination interest…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm border border-maroon/20 rounded-sm px-2 py-1.5 w-full"
            data-testid="filter-search"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-maroon/60 hover:text-maroon" aria-label="Clear search"><X size={14} /></button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-maroon/60 text-center py-8">Loading consultations…</p>
      ) : filtered.length === 0 ? (
        <p className="text-maroon/60 text-center py-8" data-testid="empty-consultations">No consultations match the current filters.</p>
      ) : (
        <ul className="space-y-3" data-testid="consultations-list">
          {filtered.map((c) => {
            const sm = statusMeta(c.status);
            return (
              <li
                key={c.id}
                className="bg-white border border-maroon/10 rounded-sm p-4 hover:border-maroon/30 transition-colors"
                data-testid={`consultation-row-${c.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-maroon">{c.name}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm border ${sm.color}`}>{sm.label}</span>
                      {c.user_id && (
                        <Link to={`/admin/members?focus=${c.user_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 px-2 py-0.5 rounded-sm hover:bg-emerald-500/20" data-testid={`consult-member-badge-${c.id}`} title="Linked to a registered member">
                          <BadgeCheck size={10} /> Member
                        </Link>
                      )}
                      {c.lead_id && (
                        <Link to={`/admin/leads?focus=${c.lead_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-terracotta/10 text-terracotta border border-terracotta/30 px-2 py-0.5 rounded-sm hover:bg-terracotta/20" data-testid={`consult-lead-link-${c.id}`} title="View linked lead">
                          <ArrowRightLeft size={10} /> Lead created
                        </Link>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-maroon/60 mt-1 flex-wrap">
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-maroon"><Mail size={12} />{c.email}</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-maroon"><Phone size={12} />{c.phone}</a>}
                      <span className="inline-flex items-center gap-1"><UsersIcon size={12} />{c.group_size} traveler{c.group_size === 1 ? '' : 's'}</span>
                      <span className="inline-flex items-center gap-1"><CalendarIcon size={12} />{c.preferred_dates}</span>
                    </div>
                    <p className="text-sm text-maroon mt-2"><span className="font-semibold">Interest:</span> {c.destination_interest}</p>
                    {c.message && <p className="text-sm text-maroon/70 italic mt-1 line-clamp-2">&ldquo;{c.message}&rdquo;</p>}
                    <textarea
                      defaultValue={c.admin_notes || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (c.admin_notes || '')) updateNotes(c.id, e.target.value);
                      }}
                      placeholder="Internal notes (saved on blur)…"
                      className="mt-2 text-xs w-full border border-maroon/15 rounded-sm px-2 py-1 resize-y min-h-[28px]"
                      data-testid={`consult-notes-${c.id}`}
                    />
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="text-[11px] text-maroon/50">{new Date(c.created_at).toLocaleDateString()}</div>
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      <select
                        value={c.status}
                        onChange={(e) => updateStatus(c.id, e.target.value)}
                        className="text-xs border border-maroon/25 rounded-sm px-2 py-1 bg-white"
                        data-testid={`consult-status-select-${c.id}`}
                      >
                        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {!c.lead_id && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => openConvert(c)}
                          className="btn-primary inline-flex items-center gap-1"
                          data-testid={`convert-to-lead-btn-${c.id}`}
                        >
                          <ArrowRightLeft size={12} /> Convert to Lead
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(c.id)}
                        className="text-red-600 hover:bg-red-50 p-1.5 rounded-sm border border-red-200"
                        aria-label="Delete"
                        data-testid={`delete-consult-${c.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Convert dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-maroon flex items-center gap-2"><ArrowRightLeft size={18} />Convert to Lead</DialogTitle>
          </DialogHeader>
          {convertTarget && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-maroon/70">
                Creating a Lead pre-filled with <strong>{convertTarget.name}</strong>'s contact info and consultation details. The original consultation will be marked as <em>completed</em>.
              </p>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-maroon/60 mb-1 block">Tie to a partner package (optional)</label>
                <select
                  value={convertPackageId}
                  onChange={(e) => setConvertPackageId(e.target.value)}
                  className="w-full text-sm border border-maroon/25 rounded-sm px-3 py-2 bg-white"
                  data-testid="convert-package-select"
                >
                  <option value="">— No package — generic quote —</option>
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>{p.title} {p.destination ? `(${p.destination})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-maroon/60 mb-1 block">Internal notes (optional)</label>
                <textarea
                  value={convertNotes}
                  onChange={(e) => setConvertNotes(e.target.value)}
                  placeholder="e.g. follow-up call scheduled for Tuesday"
                  className="w-full text-sm border border-maroon/25 rounded-sm px-3 py-2 min-h-[80px]"
                  data-testid="convert-notes-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setConvertOpen(false)} disabled={converting} className="border-maroon/30 text-maroon hover:bg-maroon/5" data-testid="convert-cancel-btn">Cancel</Button>
                <Button onClick={submitConvert} disabled={converting} className="btn-primary inline-flex items-center gap-1" data-testid="convert-submit-btn">
                  <ArrowRightLeft size={14} />{converting ? 'Converting…' : 'Create Lead'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsultationsManager;
